import '@yandex-int/messenger.widget/lib/ui/popup.css';

import React, {
    createContext,
    useCallback,
    useEffect,
    useRef,
    MouseEvent,
    useContext,
    ReactElement,
} from 'react';
import { Widget, popupUIFactory, YandexConfig } from '@yandex-int/messenger.widget';
import noop from 'lodash/noop';
import { withInvariant } from '../../utils/withInvariant';
import { withRequiredProp } from '../../utils/withRequiredProp';
import { IconHelp } from '../icon/IconHelp';
import { IconButton } from '../input/IconButton';

interface MessengerWidgetPayload {
    mount: (target: HTMLElement) => VoidFunction;
    open: (target: HTMLElement) => void;
}

const MessengerWidgetContext = createContext<MessengerWidgetPayload | null>(null);

const config = new YandexConfig({ serviceId: -1 });

const ui = popupUIFactory({});

const widget = new Widget(config).setUI(ui);

if (typeof window !== 'undefined') {
    widget.init();
}

interface MessengerWidgetProviderProps {
    guid?: string;
    children: ReactElement;
}

export const MessengerWidgetProvider = withRequiredProp<MessengerWidgetProviderProps, 'guid'>(
    (props) => {
        const { guid, children } = props;
        const open = useCallback(() => widget.show({ guid }), [guid]);
        const mount = useCallback((target: HTMLElement) => {
            ui.setPopupTarget(target);

            return () => widget.hide();
        }, []);

        useEffect(() => {
            ui.mount();
        }, []);

        return (
            <MessengerWidgetContext.Provider value={{ open, mount }}>
                {children}
            </MessengerWidgetContext.Provider>
        );
    },
    (props) => props.children,
    'guid',
);

interface MessengerButtonProps {
    onClick: () => void;
}

export const MessengerButton = withInvariant(
    (props: MessengerButtonProps) => {
        const buttonRef = useRef<HTMLButtonElement | null>(null);
        const widgetContext = useContext(MessengerWidgetContext);
        const onWidgetOpen = useCallback(
            (e: MouseEvent<HTMLButtonElement>) => {
                widgetContext?.open(e.currentTarget);
                props.onClick();
            },
            [widgetContext, props.onClick],
        );

        useEffect(() => {
            if (!buttonRef.current || !widgetContext) {
                return noop;
            }

            return widgetContext.mount(buttonRef.current);
        }, [widgetContext, buttonRef.current]);

        if (!widgetContext) {
            return null;
        }

        return (
            <IconButton ref={buttonRef} variant="fab" size="xl" onClick={onWidgetOpen}>
                <IconHelp />
            </IconButton>
        );
    },
    () => null,
);
